import { zodResolver } from '@hookform/resolvers/zod';
import { ProLightDiagramCells } from '@nexelem/icons/ProLightDiagramCells';
import { StaticModal } from '@nexelem/modals';
import { Button } from '@nexelem/components';
import { getTranslation } from '@nexelem/utils';
import { TFunction } from 'i18next';
import { LexicalEditor } from 'lexical';
import { JSX, useMemo } from 'react';
import { FormProvider, SubmitHandler, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { z } from 'zod';
import { TextFormInput } from '../../components/TextFormInput';
import { INSERT_NEW_TABLE_COMMAND } from './commands';
import { ProLightDiagramCellsRation } from './ProLightDiagramCellsRation';

const getSchema = (translation: TFunction) => {
  const t = getTranslation(translation, 'forms.validation.messages');
  return z.object({
    rows: z
      .number()
      .min(1, t('beMoreThan', { limit: 1 }))
      .max(500, t('beLessThan', { limit: 500 })),
    columns: z
      .number()
      .min(1, t('beMoreThan', { limit: 1 }))
      .max(50, t('beLessThan', { limit: 50 })),
  });
};

type FormData = z.infer<ReturnType<typeof getSchema>>;

type InsertNewTableDialogProps = {
  activeEditor: LexicalEditor;
  onClose: () => void;
};

export function InsertNewTableDialog({ activeEditor, onClose }: InsertNewTableDialogProps): JSX.Element {
  const { t: translate } = useTranslation();
  const t = useMemo(() => getTranslation(translate, 'components.richTextEditor.insertNewTableDialog'), [translate]);

  const { handleSubmit, ...form } = useForm({
    resolver: zodResolver(getSchema(translate)),
    defaultValues: {
      rows: 0,
      columns: 0,
    },
  });

  const onSubmit: SubmitHandler<FormData> = (payload: FormData) => {
    activeEditor.dispatchCommand(INSERT_NEW_TABLE_COMMAND, payload);
    onClose();
  };

  return (
    <FormProvider {...form} handleSubmit={handleSubmit}>
      <form onSubmit={handleSubmit(onSubmit)}>
        <StaticModal.Header>{t('title')}</StaticModal.Header>
        <StaticModal.Content className="flex flex-col gap-6">
          <TextFormInput
            required
            label={t('rows')}
            maxWidth={100}
            name="rows"
            placeholder="0"
            StartIcon={ProLightDiagramCells}
            type="number"
          />
          <TextFormInput
            required
            label={t('columns')}
            maxWidth={100}
            name="columns"
            placeholder="0"
            StartIcon={ProLightDiagramCellsRation}
            type="number"
          />
        </StaticModal.Content>
        <StaticModal.Footer>
          <Button type="button" variant="text" onClick={onClose}>
            {t('cancel')}
          </Button>
          <Button type="submit" variant="contained">
            {t('confirm')}
          </Button>
        </StaticModal.Footer>
      </form>
    </FormProvider>
  );
}
